import { useEffect, useState, lazy, Suspense, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import track from 'c9-tracking';
import * as ActionKeys from '../store/ActionKeys';
import * as Storage from '../classes/Storage';
import { IProps } from '../interfaces/IProps';
import { useStateValue } from '../hooks/UseStateValue';
import { reloadApplicationStateFromCache, saveApplicationStateToCache } from '../store/Actions';
import { RouterCondition } from './RouterCondition';
import usePreviousRoute from '../hooks/usePreviousRoute';

import OccupationScreenView from '../views/occupation/OccupationScreen.view';

const BornholmScreenView = lazy(
  () => import(/*webpackChunkName: 'BornholmScreenView' */ '../views/bornholm/BornholmScreen.view')
);

const CustomerUser = lazy(
  () => import(/*webpackChunkName: 'CustomerUser' */ '../views/customerUser/customerUserScreen.view')
);

const PolicyHolderScreenView = lazy(
  () => import(/*webpackChunkName: 'PolicyHolderScreenView' */ '../views/policyHolder/policyHolderScreen.view')
);

const ProductPackageScreenView = lazy(
  () => import(/*webpackChunkName: 'ProductPackageScreenView' */ '../views/productPackage/productPackageScreen.view')
);

const BasketScreenView = lazy(
  () => import(/*webpackChunkName: 'BasketScreenView' */ '../views/basket/basketScreen.view')
);

const PersonalInfoScreenView = lazy(
  () => import(/*webpackChunkName: 'PersonalInfoScreenView' */ '../views/personalInfo/personalInfoScreen.view')
);

const TerminationScreenView = lazy(
  () => import(/*webpackChunkName: 'TerminationScreenView' */ '../views/termination/TerminationScreen.view')
);

const PaymentScreenView = lazy(
  () => import(/*webpackChunkName: 'PaymentScreenView' */ '../views/payment/paymentScreen.view')
);

const PaymentFrequencyScreenView = lazy(
  () =>
    import(/*webpackChunkName: 'PaymentFrequencyScreenView' */ '../views/paymentFrequency/paymentFrequencyScreen.view')
);

const ComplianceScreenView = lazy(
  () => import(/*webpackChunkName: 'ComplianceScreenView' */ '../views/compliance/complianceScreen.view')
);

const ReceiptScreenView = lazy(
  () => import(/*webpackChunkName: 'ReceiptScreenView' */ '../views/receipt/receiptScreen.view')
);

const MockRouteViewScreen = lazy(
  () => import(/*webpackChunkName: 'MockRouteViewScreen' */ '../views/mock-route-view/MockRouteViewScreen')
);

const DynamicModalScreenView = lazy(
  () => import(/*webpackChunkName: 'DynamicModalScreenView' */ '../views/dynamic-modal/DynamicModalScreen.view')
);

const PageNotFoundScreenView = lazy(
  () => import(/*webpackChunkName: 'PageNotFoundScreenView' */ '../views/pageNotFound/PageNotFoundScreen.view')
);

const CustomerRejectionScreenView = lazy(
  () =>
    import(
      /*webpackChunkName: 'CustomerRejectionScreenView' */ '../views/customer-rejection/CustomerRejectionScreen.view'
    )
);

export enum ROUTE_NAMES {
  OCCUPATION = '/', // 0
  CUSTOMER_OR_NOT = '/kunde-eller-ej', // 1
  POLICYHOLDER = '/om-dig', // 2
  PRODUCTPACKAGE = '/pakker', // 3
  BASKET = '/indkoebskurv', // 4
  ABOUT_YOU = '/kontaktoplysninger', // 5
  TERMINATION = '/opsigelse', // 6
  PAYMENT = '/betalingsmaade', // 7
  PAYMENT_FREQUENCY = '/betalingsfrekvens', // 8
  COMPLIANCE = '/accept', // 9
  RECEIPT = '/', // 10
  CALL_ME = '/ring-mig-op',
  CALL_ME_RECEIPT = '/ring-mig-op/kvittering',
  BORNHOLM = '/bornholm',
  CUSTOMER_REJECTION = '/ring-for-pris',
  PAGE_NOT_FOUND = '/404'
}

type RouterAppProps = {
  basename: string;
  props: IProps;
  children: any;
};

const RouterApp = ({ basename, props }: RouterAppProps): JSX.Element => {
  const [states] = useStateValue();
  let location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(location);
  const [{ statusInitial, stateChange, actualStep, hasError }, dispatch] = useStateValue();
  const prevLocation = usePreviousRoute(location);
  const winpath = window.location.pathname;
  const pathname = winpath.slice(-1) === '/' && winpath.length > 1 ? winpath.slice(0, -1) : winpath;
  const isReceipt = basename.indexOf('kvittering') !== -1;
  const index = Object.values(ROUTE_NAMES).findIndex((item) => {
    const regex = new RegExp(item + '/?$');
    return regex.test(pathname);
  });
  const isSavingToCache = useRef(false);

  useEffect(() => {
    if (prevLocation !== location && location.pathname !== '/kvittering' && location.pathname !== '/' && !isReceipt) {
      track.navigate(document.URL, `Ulykkesforsikring | ${location.pathname === '/' ? '' : pathname}`);
    }
  }, [prevLocation, location, isReceipt, pathname]);

  useEffect(() => {
    if (!error && hasError.stepOne) {
      setError(true);
      navigate(ROUTE_NAMES.CALL_ME);
      setCurrentLocation((prevState) => ({
        ...prevState,
        state: {
          ...prevState.state,
          modal: states.prevLocation || prevLocation
        }
      }));
      dispatch({ type: ActionKeys.SET_PREV_ROUTE_CONFIG, payload: states.prevLocation || prevLocation });
      dispatch({ type: ActionKeys.SET_STATE_CHANGE, payload: true });
    } else if (error && hasError.stepTwo && pathname.indexOf(ROUTE_NAMES.CALL_ME_RECEIPT) === -1) {
      window.location.assign(props.urlToFrontPage);
    } else if (
      // user clicks the browser back button
      error &&
      pathname.indexOf(ROUTE_NAMES.CALL_ME) === -1 &&
      pathname.indexOf(ROUTE_NAMES.CALL_ME_RECEIPT) === -1
    ) {
      setError(false);
      setCurrentLocation((prevState) => ({
        ...prevState,
        pathname: '/',
        state: {
          ...prevState.state,
          modal: undefined
        }
      }));
      dispatch({ type: ActionKeys.SET_RESET_ERROR });
    }
  }, [
    hasError.stepOne,
    prevLocation,
    dispatch,
    states.prevLocation,
    error,
    navigate,
    location,
    hasError,
    pathname,
    props.urlToFrontPage
  ]);

  useEffect(() => {
    // Pathname === basename and status initial - clear all sessions
    if (!isReceipt && pathname === basename && statusInitial) {
      track.info([
        { key: 'clearing-cache', value: 'true' },
        { key: 'current-pathname', value: pathname },
        { key: 'is-receipt', value: isReceipt ? 'true' : 'false' },
        { key: 'basename', value: basename }
      ]);
      props.c9log.debug(`Delete sessions, onsa-personal-injury-cache-key etc., pathname: ${pathname}`, 'RouterApp');
      Storage.removeSessionItem('onsa-personal-injury-cache-key');
      Storage.removeSessionItem('onsa-personal-injury-receipt-email');
      Storage.removeSessionItem('onsa-personal-injury-cpr-mismatch');
    } else if (statusInitial && !hasError) {
      track.info([
        { key: 'restoring-from-cache', value: 'true' },
        { key: 'current-pathname', value: pathname },
        { key: 'is-receipt', value: isReceipt ? 'true' : 'false' },
        { key: 'basename', value: basename }
      ]);
      props.c9log.debug(`Restoring application state from cache, pathname: ${pathname}`, 'RouterApp');
      reloadApplicationStateFromCache(props, dispatch, props.c9log);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusInitial, dispatch, basename, pathname, hasError, isReceipt]);

  useEffect(() => {
    if (index !== actualStep && index !== -1) {
      dispatch({ type: ActionKeys.SET_PROGRESS, payload: index });
    }

    if (stateChange && !isSavingToCache.current) {
      isSavingToCache.current = true;
      dispatch({ type: ActionKeys.SET_STATE_CHANGE, payload: false });
      (async () => {
        props.c9log.debug(`Saving application state to cache, url: ${window.location.href}`, 'Actions');
        await saveApplicationStateToCache(props, states, props.c9log);
        props.c9log.debug(`Application state saved to cache, url: ${window.location.href}`, 'Actions');
        isSavingToCache.current = false;
      })();
    }
  }, [props, dispatch, stateChange, states, index, actualStep]);

  let showModal = currentLocation.state && (currentLocation.state as any).modal;

  if (showModal) {
    location = hasError.stepOne
      ? { ...location, pathname: ROUTE_NAMES.CALL_ME }
      : { ...location, pathname: ROUTE_NAMES.CALL_ME_RECEIPT };
    location = { ...location, state: currentLocation.state };
  }

  return !statusInitial || isReceipt || actualStep > 0 ? (
    <>
      <Suspense fallback={<></>}>
        <Routes location={location}>
          <Route path={`${ROUTE_NAMES.BORNHOLM}`} element={<BornholmScreenView {...props} />} />

          <Route
            path={isReceipt ? `${ROUTE_NAMES.RECEIPT}` : `${ROUTE_NAMES.OCCUPATION}`}
            element={
              <RouterCondition {...props}>
                {isReceipt ? <ReceiptScreenView {...props} /> : <OccupationScreenView {...props} />}
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.CUSTOMER_OR_NOT}`}
            element={
              <RouterCondition {...props}>
                <CustomerUser {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.POLICYHOLDER}`}
            element={
              <RouterCondition {...props}>
                <PolicyHolderScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.PRODUCTPACKAGE}`}
            element={
              <RouterCondition {...props}>
                <ProductPackageScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.BASKET}`}
            element={
              <RouterCondition {...props}>
                <BasketScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.ABOUT_YOU}`}
            element={
              <RouterCondition {...props}>
                <PersonalInfoScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.TERMINATION}`}
            element={
              <RouterCondition {...props}>
                <TerminationScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.PAYMENT}`}
            element={
              <RouterCondition {...props}>
                <PaymentScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.PAYMENT_FREQUENCY}`}
            element={
              <RouterCondition {...props}>
                <PaymentFrequencyScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.COMPLIANCE}`}
            element={
              <RouterCondition {...props}>
                <ComplianceScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.CUSTOMER_REJECTION}`}
            element={
              <RouterCondition {...props}>
                <CustomerRejectionScreenView {...props} />
              </RouterCondition>
            }
          />

          <Route
            path={`${ROUTE_NAMES.CALL_ME}`}
            element={
              <>
                {showModal && <DynamicModalScreenView {...props} />}
                <RouterCondition {...props}>
                  <MockRouteViewScreen />
                </RouterCondition>
              </>
            }
          />

          <Route
            path={`${ROUTE_NAMES.CALL_ME_RECEIPT}`}
            element={
              <>
                {showModal && <DynamicModalScreenView {...props} />}
                <RouterCondition {...props}>
                  <MockRouteViewScreen />
                </RouterCondition>
              </>
            }
          />
          <Route path={`${ROUTE_NAMES.PAGE_NOT_FOUND}`} element={<PageNotFoundScreenView {...props} />} />
          <Route path="*" element={<Navigate replace to={ROUTE_NAMES.PAGE_NOT_FOUND} />} />
        </Routes>
      </Suspense>
    </>
  ) : (
    <></>
  );
};

export default RouterApp;
